import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, FormattedMessage } from 'react-intl';

import {
	BookingList,
	Pagination,
	RuSecondaryButton,
	Spinner,
	Warning,
	Tabs,
	Paragraph,
} from '@silverrailtech/srtlibrary';
import { withRouter } from 'react-router-dom';
import { useSelector } from 'react-redux';
import messages from '../../../messagesCustomerAccounts';

import {
	BookingCreateWrapper,
	BookingTabWrapper,
	PaginationWrapper,
} from '../../ManageCustomerAccountStyled';
import { getBasePath } from '../../../../utils';
import { getCustomerAccountData } from '../../../../api';
import ExtendedSnackbar from '../../../../components/ExtendedSnackbar/ExtendedSnackbar';
import { getStationDataApi } from '../../../../components/apiComponents';
import useAuth from '../../../../hooks/useAuth';
import useRequestor from '../../../../hooks/useRequestor';

import { BookingTabRow } from './BookingsStyled';

const defaultPagination = {
	page: 0,
	size: 5,
	totalPages: 0,
	totalElements: 0,
};

const Bookings = ({ intl: { formatMessage }, history }) => {
	const { getAuthHeaders, accountId } = useAuth();
	const { apiCallWithAuthRetry } = useRequestor();

	const [currentTab, setCurrentTab] = useState('all');
	const [bookings, setBookings] = useState([]);
	const [pagination, setPagination] = useState(defaultPagination);
	const [isPending, setIsPending] = useState(true);
	const [error, setError] = useState('');

	const tabs = [
		{
			id: 'all',
			label: formatMessage(messages.lblBookingsAllTab),
			testId: 'bookingTabAllButton',
		},
		{
			id: 'active',
			label: formatMessage(messages.lblBookingsActiveTab),
			testId: 'bookingTabActiveButton',
		},
		{
			id: 'used',
			label: formatMessage(messages.lblBookingsUsedTab),
			testId: 'bookingTabUsedButton',
		},
	];

	const authSettings = useSelector((state) => state.get('auth'));
	const securityCategories = authSettings.get('securityAuthorizedCategories');
	const canCreateNewBooking = securityCategories.includes('shopping');

	// Cache for station names to avoid redundant API calls
	const stationCache = useRef({});

	const fetchStationName = (code) => {
		if (stationCache.current[code]) {
			return Promise.resolve([code, stationCache.current[code]]);
		}
		return new Promise((resolve) => {
			getStationDataApi(
				code,
				() => {
					stationCache.current[code] = code;
					resolve([code, code]);
				},
				(response) => {
					const stationName =
						response?.successResponse?.data?.StationAttrs
							?.StationAttrGeneralInfo?.StationAttrGeneralInfoStationName ??
						code;
					stationCache.current[code] = stationName;
					resolve([code, stationName]);
				},
			);
		});
	};

	useEffect(() => {
		if (!accountId) {
			setError('Failed to load bookings');
			setIsPending(false);
			return;
		}

		const fetchBookings = () => {
			setIsPending(true);
			apiCallWithAuthRetry({
				apiCall: (onError, onSuccess, refreshedHeaders) => {
					getCustomerAccountData(
						{
							status: currentTab.toUpperCase(), // Available values for Status query: ACTIVE, USED, ALL
							page: pagination.page,
							size: pagination.size,
						},
						refreshedHeaders || getAuthHeaders(),
						`/${accountId}/bookings`,
						onError,
						onSuccess,
					);
				},
				onError: () => {
					// eslint-disable-next-line no-console

					setError('Failed to load bookings');
					setIsPending(false);
				},
				onSuccess: async (response) => {
					const initialBookings =
						response?.content?.map((booking) => ({
							...booking,
							passenger: booking.passenger ?? 1,
						})) ?? [];

					const uniqueCodes = [
						...new Set(
							initialBookings
								.flatMap((b) => [b.origin, b.destination])
								.filter(Boolean),
						),
					];
					const results = await Promise.all(uniqueCodes.map(fetchStationName));
					const lookup = Object.fromEntries(results);

					const finalBookings = initialBookings.map((booking) => ({
						...booking,
						originName: lookup[booking.origin] || booking.origin || '',
						destinationName:
							lookup[booking.destination] || booking.destination || '',
					}));

					setBookings(finalBookings);
					setPagination((prev) => ({
						...prev,
						totalPages: response.totalPages,
						totalElements: response.totalElements,
					}));
					setIsPending(false);
				},
			});
		};

		fetchBookings();
	}, [
		accountId,
		getAuthHeaders,
		pagination.page,
		currentTab,
		apiCallWithAuthRetry,
	]);

	const handlePageChange = (_, newPage) => {
		setPagination((prev) => ({
			...prev,
			page: newPage - 1,
		}));
	};

	const renderBookingError = useMemo(() => {
		switch (currentTab) {
			case 'active':
				return formatMessage(messages.lblActiveBookingsEmptyState);
			case 'used':
				return formatMessage(messages.lblUsedBookingsEmptyState);
			default:
				return formatMessage(messages.lblBookingsEmptyState);
		}
	}, [currentTab]);

	return (
		<>
			<div className="container">
				<div className="row">
					<div className="col-12">
						<h1>
							<FormattedMessage {...messages.lblBookingsTitle} />
						</h1>
					</div>
				</div>
			</div>

			<BookingTabWrapper>
				<div className="container">
					<BookingTabRow className="row align-items-center">
						<div className="col-8">
							<Tabs
								tabs={tabs}
								activeTab={currentTab}
								onTabChange={setCurrentTab}
							/>
						</div>

						<div className="col-4">
							<Paragraph
								className="text-right"
								margin="0"
								fontSize="13px"
								color="primary_1"
							>
								<FormattedMessage
									{...messages.lblBookingsShowingCount}
									values={{
										shown:
											pagination.totalPages === 0 ? 0 : pagination.page + 1,
										total: pagination.totalPages,
									}}
								/>
							</Paragraph>
						</div>
					</BookingTabRow>
				</div>
			</BookingTabWrapper>

			<div className="container">
				{bookings.length > 0 && (
					<div className="row">
						<div className="col-12">
							<BookingList
								bookings={bookings}
								onBookingClick={(bookingId) => {
									history.push(`${getBasePath()}booking/Manage/${bookingId}`);
								}}
								buttonPosition="space-between"
								isAgent
							/>
						</div>
					</div>
				)}
				{bookings.length === 0 && (
					<Warning variant="noteworthy" text={renderBookingError} />
				)}
			</div>
			{pagination.totalPages > 1 && (
				<PaginationWrapper>
					<Pagination
						count={pagination.totalPages}
						page={pagination.page + 1}
						onChange={handlePageChange}
					/>
				</PaginationWrapper>
			)}

			{canCreateNewBooking && (
				<div className="container">
					<div className="row">
						<div className="col-12">
							<BookingCreateWrapper>
								<RuSecondaryButton
									color="positive"
									data-testid="createNewBookingButton"
									aria-label={formatMessage(
										messages.lblBookingsCreateBookingButton,
									)}
									onClick={() => {
										history.push(`${getBasePath()}shopping/Search`);
									}}
								>
									<FormattedMessage
										{...messages.lblBookingsCreateBookingButton}
									/>
								</RuSecondaryButton>
							</BookingCreateWrapper>
						</div>
					</div>
				</div>
			)}

			<ExtendedSnackbar
				id="srtBookingSnackBar"
				open={error !== ''}
				message={error}
				onClose={() => setError('')}
			/>
			{isPending && <Spinner />}
		</>
	);
};

Bookings.propTypes = {
	intl: PropTypes.object.isRequired,
	history: PropTypes.object,
	authHeaders: PropTypes.object,
	accountId: PropTypes.string,
};

export default withRouter(injectIntl(Bookings));
